import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, LogOut, Menu } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useSession } from "@/account/lib/auth-client";
import { NosibleBrandLockup } from "./NosibleBrandLockup";

// Keep order, labels and disabled state aligned with nosible-world's
// app/_components/world-navigation.ts. These are full cross-site navigations.
const WORLD_NAV_ITEMS = [
  { id: "chaos", label: "Firehose", href: "https://nosible.world/chaos" },
  { id: "search", label: "Search", href: null },
  { id: "world", label: "World", href: "https://nosible.world/world" },
  { id: "atlas", label: "Atlas", href: "https://nosible.world/atlas" },
  { id: "trend", label: "Risks", href: "https://nosible.world/trend" },
  { id: "build", label: "Build", href: "https://docs.nosible.com/" },
  { id: "status", label: "Status", href: "https://status.nosible.com/" },
] as const;

const ACCOUNT_NAV_ITEMS = [
  { label: "Overview", to: "/" },
  { label: "Profile & email", to: "/account" },
  { label: "Organizations", to: "/organizations" },
  { label: "Subscriptions", to: "/subscriptions" },
  { label: "Your API Keys", to: "/api-access" },
] as const;

function WorldNavigation({ mobile = false, onNavigate }: { mobile?: boolean; onNavigate?: () => void }) {
  return (
    <nav
      id={mobile ? "world-mobile-nav-menu" : undefined}
      className={mobile ? "world-site-mobile-nav" : "world-site-nav"}
      aria-label={mobile ? "NOSIBLE World mobile menu" : "NOSIBLE World menu"}
    >
      {WORLD_NAV_ITEMS.map(({ id, label, href }) => href === null ? (
        <button key={id} type="button" className="world-nav-link" disabled aria-disabled="true" title="Search is not available yet">{label}</button>
      ) : (
        <a key={id} href={href} className="world-nav-link" onClick={onNavigate}>{label}</a>
      ))}
    </nav>
  );
}

/** The World header, adapted only at the account session/router boundary. */
export function WorldSiteHeader({ onLogout }: { onLogout?: () => Promise<void> }) {
  const authenticated = Boolean(onLogout);
  const { data: session } = useSession();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [contextOpen, setContextOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const headerRef = useRef<HTMLElement>(null);
  const accountButtonRef = useRef<HTMLButtonElement>(null);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const contextButtonRef = useRef<HTMLButtonElement>(null);
  const contextMenuRef = useRef<HTMLElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setAccountOpen(false);
    setContextOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!mobileOpen && !accountOpen && !contextOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setMobileOpen(false);
      setAccountOpen(false);
      setContextOpen(false);
      if (accountOpen) accountButtonRef.current?.focus();
      if (contextOpen) contextButtonRef.current?.focus();
    };
    const closeOnPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (headerRef.current?.contains(target) || accountMenuRef.current?.contains(target) || contextMenuRef.current?.contains(target)) return;
      setMobileOpen(false);
      setAccountOpen(false);
      setContextOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnPointerDown);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnPointerDown);
    };
  }, [mobileOpen, accountOpen, contextOpen]);

  useEffect(() => {
    if (!accountOpen) {
      setMenuPosition(null);
      return;
    }
    const updatePosition = () => {
      const rect = accountButtonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPosition({
        top: rect.bottom + 8,
        left: Math.max(8, Math.min(window.innerWidth - 224 - 8, rect.right - 224)),
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [accountOpen]);

  const identity = session?.user?.name?.trim() || session?.user?.email?.trim() || "NOSIBLE";
  const words = identity.split(/\s+/).filter(Boolean);
  const initials = (words.length >= 2 ? `${words[0]?.[0]}${words[1]?.[0]}` : identity.slice(0, 2)).toUpperCase();

  return (
    <>
      <header ref={headerRef} className="world-site-header" role="banner">
        {authenticated && (
          <button
            ref={contextButtonRef}
            className="world-site-menu-button world-site-context-toggle"
            type="button"
            aria-label={contextOpen ? "Close account navigation" : "Open account navigation"}
            aria-expanded={contextOpen}
            aria-controls="world-account-context-menu"
            onClick={() => { setContextOpen(!contextOpen); setMobileOpen(false); setAccountOpen(false); }}
          >
            <Menu size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        )}
        <a className="world-site-brand" href="https://nosible.com/" aria-label="NOSIBLE home">
          <NosibleBrandLockup classPrefix="world" />
        </a>
        <WorldNavigation />
        <div className="world-site-utilities">
          <div className="world-site-language-wrap">
            <div className="world-site-language-frame">
              {/* Account screens are English-only, like the Atlas header. */}
              <button className="world-site-language-button" type="button" aria-label="Event language" aria-haspopup="listbox" aria-expanded="false" disabled>
                <span>EN</span><ChevronDown size={12} strokeWidth={1.5} aria-hidden="true" />
              </button>
            </div>
          </div>
          {authenticated ? (
            <button
              ref={accountButtonRef}
              className="world-site-account-button"
              type="button"
              aria-label="Account menu"
              aria-expanded={accountOpen}
              aria-controls="world-account-menu"
              title={session?.user?.email}
              onClick={() => { setAccountOpen(!accountOpen); setMobileOpen(false); setContextOpen(false); }}
            >
              {session?.user?.image ? <img src={session.user.image} alt="" referrerPolicy="no-referrer" /> : <span>{initials}</span>}
            </button>
          ) : (
            <Link className="world-site-login-button" to="/login">Log in</Link>
          )}
          <button
            className="world-site-menu-button world-site-mobile-toggle"
            type="button"
            aria-label={mobileOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={mobileOpen}
            aria-controls="world-mobile-nav-menu"
            onClick={() => { setMobileOpen(!mobileOpen); setAccountOpen(false); setContextOpen(false); }}
          >
            <Menu size={16} strokeWidth={1.5} aria-hidden="true" />
          </button>
        </div>
        {mobileOpen && <WorldNavigation mobile onNavigate={() => setMobileOpen(false)} />}
      </header>
      {authenticated && (
        <nav className="world-site-tablet-context" aria-label="Account navigation">
          {ACCOUNT_NAV_ITEMS.map(({ to, label }) => <Link key={to} to={to} aria-current={pathname === to ? "page" : undefined}>{label}</Link>)}
        </nav>
      )}
      {authenticated && contextOpen && createPortal(
        <nav ref={contextMenuRef} id="world-account-context-menu" className="world-site-context-menu" aria-label="Account mobile navigation">
          {ACCOUNT_NAV_ITEMS.map(({ to, label }) => <Link key={to} to={to} aria-current={pathname === to || (to !== "/" && pathname.startsWith(`${to}/`)) ? "page" : undefined} onClick={() => setContextOpen(false)}>{label}</Link>)}
          <button type="button" onClick={() => { setContextOpen(false); void onLogout?.(); }}><LogOut size={14} aria-hidden="true" />Sign out</button>
        </nav>, document.body,
      )}
      {authenticated && accountOpen && menuPosition && createPortal(
        <div ref={accountMenuRef} id="world-account-menu" className="nosible-account-menu world-site-account-menu" role="menu" style={menuPosition}>
          <div className="nosible-account-menu__identity">
            <strong>{session?.user?.name?.trim() || session?.user?.email || "NOSIBLE account"}</strong>
            {session?.user?.name?.trim() ? <span>{session.user.email}</span> : null}
          </div>
          <Link className="nosible-account-menu__action" to="/" role="menuitem" onClick={() => setAccountOpen(false)}>Manage account</Link>
          <button className="nosible-account-menu__action" type="button" role="menuitem" onClick={() => { setAccountOpen(false); void onLogout?.(); }}>Log out of browser</button>
        </div>, document.body,
      )}
    </>
  );
}
